import { useMemo } from 'react'

/**
 * Hook to detect and extract subdomain from current hostname
 * 
 * @returns {string | null} - Subdomain if present, null if on root domain
 * 
 * Examples:
 * - localhost:5173 → null (root domain)
 * - fctrz.localhost:5173 → 'fctrz' (subdomain)
 * - app.example.com → 'app' (subdomain)
 * - example.com → null (root domain)
 */
export function useSubdomain(): string | null {
  return useMemo(() => {
    const hostname = window.location.hostname
    const parts = hostname.split('.')
    
    // Single part hostname (e.g., 'localhost') → no subdomain
    if (parts.length === 1) {
      return null
    }
    
    // For localhost development (e.g., fctrz.localhost)
    if (parts[parts.length - 1] === 'localhost') {
      return parts.length > 1 ? parts[0] : null
    }
    
    // For production domains (e.g., fctrz.example.com)
    // Assume root domain is last two parts (example.com)
    // Everything before that is subdomain
    if (parts.length > 2) {
      // Return first part as subdomain
      return parts[0]
    }
    
    // Two-part domain (example.com) → no subdomain
    return null
  }, [])
}

/**
 * Hook to check if currently on root domain (no subdomain)
 */
export function useIsRootDomain(): boolean {
  const subdomain = useSubdomain()
  return subdomain === null
}

/**
 * Hook to get the base domain without subdomain
 * 
 * @returns {string} - Base domain (e.g., 'localhost:5173' or 'example.com')
 */
export function useBaseDomain(): string {
  return useMemo(() => {
    const hostname = window.location.hostname
    const port = window.location.port
    const parts = hostname.split('.')
    
    // For localhost
    if (parts[parts.length - 1] === 'localhost') {
      return port ? `localhost:${port}` : 'localhost'
    }
    
    // For production domains, return last two parts
    if (parts.length > 2) {
      const baseDomain = parts.slice(-2).join('.')
      return port ? `${baseDomain}:${port}` : baseDomain
    }
    
    return port ? `${hostname}:${port}` : hostname
  }, [])
}
